import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { ModalRedefinirSenhaComponent } from 'src/app/shared/components/modal-redefinir-senha/modal-redefinir-senha.component';
import { PerfilEnum } from 'src/app/shared/enums/perfil.enum';
import { AuthService } from 'src/app/shared/services/auth.service';
import { NotificacoesAgendadasService } from 'src/app/shared/services/notificacoesAgendadas.service';
import { PratoService } from 'src/app/shared/services/prato.service';
import { RestauranteService } from 'src/app/shared/services/restaurante.service';
import { WebSocketService } from 'src/app/shared/services/websocket.service';

interface Notificacao {
  id: string;
  titulo: string;
  mensagem: string;
  tipo: 'success' | 'error' | 'warning' | 'info';
  dataHora: Date;
  lida: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule, TablerIconsModule, MaterialModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  showFiller = false;
  showProButtons = false;

  nome: string = '';
  nomeFantasia: string = '';
  perfil: string = '';
  progressoImportacaoRestaurante: number = 0;
  progresso: any;
  progressoTitulo = '';
  private _progressoSubRestaurantes!: Subscription;
  private _progressoSubPratos!: Subscription;
  private _notificacaoSub!: Subscription;

  // Notificações
  notificacoes: Notificacao[] = [];
  notificacoesNaoLidas: number = 0;
  carregandoNotificacoes: boolean = false;

  constructor(
    public dialog: MatDialog,
    private router: Router,
    private restauranteService: RestauranteService,
    private pratoService: PratoService,
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private notificacoesAgendadasService: NotificacoesAgendadasService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    const perfil =
      localStorage.getItem('perfil') || sessionStorage.getItem('perfil');
    this.nome =
      localStorage.getItem('nome') || sessionStorage.getItem('nome') || '';
    this.perfil =
      localStorage.getItem('perfil') || sessionStorage.getItem('perfil') || '';
    if (this.perfil === PerfilEnum.RESTAURANTE) {
      this.nomeFantasia =
        localStorage.getItem('nomeFantasia') ||
        sessionStorage.getItem('nomeFantasia') ||
        '';
      this.showProButtons = !!(this.authService.planoPro !== 'true');
    }
  }

  async ngOnInit() {
    // Adicionar notificação de teste
    console.log('🚀 Componente iniciado');
    console.log('📋 Notificações iniciais:', this.notificacoes.length);

    // Carregar notificações existentes do usuário
    await this.carregarNotificacoesExistentes();

    // Inscrever no serviço de WebSocket
    this._notificacaoSub = this.webSocketService
      .onNotificacao()
      .subscribe((data: any) => {
        console.log('🔔 Notificação recebida no header:', data);

        if (data) {
          this.ngZone.run(() => {
            const novaNotificacao: Notificacao = {
              id: data.id || Date.now().toString(),
              titulo: data.titulo || 'Nova Notificação',
              mensagem: data.mensagem || data.message || '',
              tipo: this.mapearTipo(data.tipo),
              dataHora: new Date(),
              lida: false,
            };

            console.log('📝 Notificação criada:', novaNotificacao);

            // Adicionar no início da lista
            this.notificacoes = [novaNotificacao, ...this.notificacoes];
            this.notificacoesNaoLidas++;

            console.log('📊 Total de notificações:', this.notificacoes.length);
            console.log('🔢 Não lidas:', this.notificacoesNaoLidas);
            console.log('📦 Array completo:', this.notificacoes);

            // Limitar a 50 notificações
            if (this.notificacoes.length > 50) {
              this.notificacoes = this.notificacoes.slice(0, 50);
            }

            // Forçar detecção de mudanças
            this.cdr.detectChanges();
          });
        }
      });
    this._progressoSubRestaurantes =
      this.restauranteService.progressoImportadorRestaurantes.subscribe((p) => {
        this.progressoTitulo = 'Processando restaurantes';
        this.progresso = p;
      });
    this._progressoSubPratos =
      this.pratoService.progressoImportadorPratos.subscribe((p) => {
        this.progressoTitulo = 'Processando pratos';
        this.progresso = p;
      });
  }

  async carregarNotificacoesExistentes() {
    try {
      this.carregandoNotificacoes = true;
      const userId = localStorage.getItem('id') || sessionStorage.getItem('id');

      if (!userId) {
        console.log(
          '⚠️ Usuário não identificado, não é possível carregar notificações',
        );
        return;
      }

      // Determinar qual ID usar para verificar se foi lida
      let idParaVerificarLeitura: string | null;
      if (this.perfil === PerfilEnum.RESTAURANTE) {
        idParaVerificarLeitura =
          localStorage.getItem('restauranteId') ||
          sessionStorage.getItem('restauranteId');
      } else {
        idParaVerificarLeitura = userId;
      }

      console.log('🔍 Carregando notificações do usuário:', userId);
      const notificacoesApi =
        await this.notificacoesAgendadasService.buscarNotificacoesUsuario(
          userId,
        );

      console.log('📥 Notificações recebidas da API:', notificacoesApi);

      if (notificacoesApi && Array.isArray(notificacoesApi)) {
        this.notificacoes = notificacoesApi.map((n: any) => {
          // Verificar se o ID do usuário/restaurante está no array de lidos
          const foiLida =
            n.lidos && Array.isArray(n.lidos) && idParaVerificarLeitura
              ? n.lidos.includes(idParaVerificarLeitura)
              : false;

          return {
            id: n.id || n._id,
            titulo: n.titulo,
            mensagem: n.mensagem,
            tipo: this.mapearTipo(n.tipo),
            dataHora: new Date(n.dataAgendamento || n.createdAt),
            lida: foiLida,
          };
        });

        // Contar não lidas
        this.notificacoesNaoLidas = this.notificacoes.filter(
          (n) => !n.lida,
        ).length;

        console.log('✅ Notificações carregadas:', this.notificacoes.length);
        console.log('🔢 Não lidas:', this.notificacoesNaoLidas);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar notificações:', error);
    } finally {
      this.carregandoNotificacoes = false;
    }
  }

  ngOnDestroy() {
    if (this._notificacaoSub) {
      this._notificacaoSub.unsubscribe();
    }
    if (this._progressoSubRestaurantes) {
      this._progressoSubRestaurantes.unsubscribe();
    }
    if (this._progressoSubPratos) {
      this._progressoSubPratos.unsubscribe();
    }
  }

  async logout() {
    this.authService.limparAutorizacao();
    await this.router.navigate(['/auth/login']);
  }

  async alterarSenha() {
    const dialogRef = this.dialog.open(ModalRedefinirSenhaComponent, {
      data: {},
      hasBackdrop: true,
      autoFocus: false,
      panelClass: 'md:w-[30%]',
    });
    dialogRef.afterClosed().subscribe(async (resultado) => {});
  }

  private mapearTipo(tipo: string): 'success' | 'error' | 'warning' | 'info' {
    if (!tipo) return 'info';
    const tipoLower = tipo.toLowerCase();
    if (tipoLower === 'success' || tipoLower === 'sucesso') return 'success';
    if (tipoLower === 'error' || tipoLower === 'erro') return 'error';
    if (tipoLower === 'warning' || tipoLower === 'aviso') return 'warning';
    return 'info';
  }

  marcarComoLida(notificacao: Notificacao) {
    if (!notificacao.lida) {
      notificacao.lida = true;
      this.notificacoesNaoLidas--;
    }
  }

  async marcarTodasComoLidas() {
    try {
      const id =
        localStorage.getItem('userId') || sessionStorage.getItem('userId');

      if (!id) {
        console.log('⚠️ ID não identificado');
        return;
      }

      // Chamar endpoint do backend
      await this.notificacoesAgendadasService.marcarTodasComoLidas(id);

      // Atualizar estado local
      this.notificacoes.forEach((n) => (n.lida = true));
      this.notificacoesNaoLidas = 0;

      console.log('✅ Todas as notificações marcadas como lidas');
    } catch (error) {
      console.error('❌ Erro ao marcar notificações como lidas:', error);
      // Atualizar localmente mesmo se o backend falhar
      this.notificacoes.forEach((n) => (n.lida = true));
      this.notificacoesNaoLidas = 0;
    }
  }

  removerNotificacao(notificacao: Notificacao) {
    const index = this.notificacoes.indexOf(notificacao);
    if (index > -1) {
      if (!notificacao.lida) {
        this.notificacoesNaoLidas--;
      }
      this.notificacoes.splice(index, 1);
    }
  }

  limparTodasNotificacoes() {
    this.notificacoes = [];
    this.notificacoesNaoLidas = 0;
  }
}
